/**
 * @ngdoc Directive
 * @name vrmSummaryGraphWidget
 * @example
 *
 * <vrmSummaryGraphWidget installation="installation" type="TempSummaryAndGraph" />
 *
 */
import { sprintf } from '@/angular/libs/sprintf';
import { mixinZoomState } from '@/angular/modules/vrm-components/graph-widget/zoomstate';
import { cloneDeep } from 'lodash';
import { handleLocalStorageBool, handleInitialLocalStorageBool } from '@/angular/utils/handleLocalStorage'


angular.module('vrm.components')
    .directive('vrmSummaryGraphWidget', vrmSummaryGraphWidget)
    .constant('AdvancedSummaryGraphWidgetConfigsSummaries', {
        "DCMeter": {
            renderSmall: true,
            hide: [
                "dE"
            ],
            extended: {
                "advanced.energy_delta": function ($scope) {
                    var total = null;
                    if ($scope.chart && $scope.chart.series[0]) {
                        _.each($scope.chart.series[0].data, function (record) {
                            if (record[1]) {
                                total += record[1];
                            }
                        });
                    }

                    if (total) {
                        total = sprintf($scope.getField("dE").formatWithUnit.replace('F', 'f'), total);
                    }
                    return [total === null ? '-' : total];
                }
            }
        },
        "TempSummaryAndGraph": {
            renderSmall: false,
            hide: [
                "tsP", "tso", "tsc", "tst", "tsS"
            ]
        },
        "MeteorologicalSensor": {
            renderSmall: false,
            hide: []
        },
        "GlobalLinkSummary": {
            renderSmall: false,
            hide: []
        }
    })
    .constant('AdvancedSummaryGraphWidgetConfigsGraphs', (function () {
        // The axis config is mainly determined by the unit plotted, so there is a grouped config per unit
        var pressureConfig = {
            maxPadding: 0.2, // 20% percent padding
            minPadding: 0,
            tickAmount: 6,
        };

        var humidityConfig = {
            maxPadding: 0.2, // 20% percent padding
            minPadding: 0,
            tickAmount: 11,
            min: 0,
            max: 100
        };

        var temperature11TicksConfig = {
            maxPadding: 0.2, // 20% percent padding
            minPadding: 0,
            forceTickAt0: true, // Show tick at 0, and allow negative values
            tickAmount: 11
        };

        var temperature10TicksConfig = {
            maxPadding: 0.2, // 20% percent padding
            minPadding: 0,
            forceTickAt0: true, // Show tick at 0, and allow negative values
            tickAmount: 10
        };

        var powerConfig = {
            minPadding: 0,
            maxPadding: 0.1, // 10% percent padding
            forceTickAt0: true, // Show tick at 0, and allow negative values
            tickAmount: 10
        };

        var windSpeedConfig = {
            forceTickAt0: true, // Show tick at 0, and allow negative values
            minPadding: 0.2,
            maxPadding: 0.2,
            tickAmount: 10
        };

        var signalRSSIConfig = {
            forceTickAt0: true, // Show tick at 0, and allow negative values
            minPadding: 0,
            maxPadding: 0.1,
            tickAmount: 10,
            minTickInterval: 5
        };

        // Definition of all charts widgets + used attributes used in mixed widgets
        return {
            'DCMeter': [
                {
                    extraOptions: {
                        options: {
                            plotOptions: {
                                column: {
                                    borderWidth: 0,
                                    shadow: false
                                },
                            }
                        }
                    },
                    fields: {
                        'ddE': {
                            serieOptions: {
                                type: 'column'
                            }
                        }
                    }
                }
            ],
            'TempSummaryAndGraph': [{
                extraOptions: {
                    yAxis: [
                        temperature11TicksConfig,
                        humidityConfig,
                        pressureConfig,
                    ],
                    options: {
                        chart: {
                            marginTop: 10,
                            marginLeft: 60,
                            marginRight: 80
                        }
                    }
                },
                fields: {
                    'tsT': null,
                    'tsH': {
                        yAxis: 1
                    },
                    'tsB': {
                        yAxis: 2
                    },
                }
            }],
            'MeteorologicalSensor': [{
                extraOptions: {
                    yAxis: [
                        powerConfig,
                        windSpeedConfig,
                        temperature10TicksConfig
                    ],
                    options: {
                        chart: {
                            marginTop: 10,
                            marginLeft: 60,
                            marginRight: 80
                        }
                    }
                },
                fields: {
                    'msI': null,
                    'msW': {
                        yAxis: 1
                    },
                    'msT': {
                        yAxis: 2
                    },
                }
            }],
            'GlobalLinkSummary': [{
                extraOptions: {
                    yAxis: [
                        signalRSSIConfig,
                    ],
                    options: {
                        chart: {
                            marginTop: 10,
                            marginLeft: 60,
                            marginRight: 80
                        }
                    }
                },
                fields: {
                    'pcR': null,
                }
            }]
        }
    })());

/* @ngInject */
function vrmSummaryGraphWidget() {

    return {
        restrict: 'E',
        template: require('@/angular/modules/vrm-components/summary-graph-widget/summary-graph-widget.template.html'),
        controller: controller,
        scope: {
            iconClass: '=',
            customName: '=',
            installation: '=',
            type: '=',
            loading: '=',
            instance: '=',
            title: '=',
            subTitle: '=',
            tooltip: '=',
            graphData: '=',
            zoomstate: '=',
            range: '=',
            widgetId: '=',
            isMobile: '=',
            hasTouchEnabled: '=',
            yAxisProperties: '=',
            size: '=',
            itemState: '=?',
            persistentMode: '='
        }
    };

    /* @ngInject */
    function controller(MQTTRealtimeService, $scope, ApiService, AdvancedSummaryGraphWidgetConfigsSummaries, $q, GraphService, AdvancedSummaryGraphWidgetConfigsGraphs, locale, vrmConfig, WhitelabelService, localStorageService) {
        // Graph methods
        let loaded = false;
        $scope.inview = false;
        $scope.setWatcher = true;

        $scope.$watch('type', function () {
            $scope.meta = AdvancedSummaryGraphWidgetConfigsGraphs[$scope.type];
            $scope.current = $scope.meta[0];
        });

        $scope.setCurrent = function (meta) {
            $scope.current = meta;
            $scope.chart = GraphService.getAdvancedConfig($scope.current, getGraphColors());
        };

        $scope.$watch('loading', function (loading) {
            if ($scope.chart) {
                $scope.chart.loading = loading;
            }
        });

        $scope.$watch('size', () => {
            if (!$scope.chart || !$scope.chart.getHighcharts) return;
            $scope.chart.getHighcharts().reflow();
        });

        $scope.formatSeriesTitle = function (meta) {
            let result = '';
            if (meta.description) {
                result += ` ${meta.description}`;
            }
            if (meta.formatWithUnit) {
                const unit = meta.formatWithUnit
                    .replace(meta.formatValueOnly, '')
                    .replace('%%', '%')
                    .replace(/%\.\dF/, '')
                    .trim();
                if (unit) {
                    result += ` (${unit})`;
                }
            }
            return result;
        };

        $scope.toggle = function ($index) {
            const series = $scope.chart.series[$index];
            series.visible = !series.visible;
        };

        $scope.seriesLabelStyle = function ($index) {
            const colors = WhitelabelService.getGraphColors();
            const colorByIndex = [
                colors["blue"],
                colors["orange"],
                colors["black"],
                colors["green"],
            ];
            let opacity = 1;
            if (
                $scope.chart &&
                $scope.chart.series[$index] &&
                !$scope.chart.series[$index].visible
            ) {
                opacity = 0.5;
            }
            return { color: colorByIndex[$index], opacity: opacity };
        };

        $scope.widgetTitle = function () {
            let title = $scope.title;
            if ($scope.customName) {
                title = `${$scope.customName} - ` + title;
            }
            if (parseInt($scope.instance)) {
                title += ` [${$scope.instance}]`;
            }
            return title;
        };

        $scope.popup = {
            elem: null,
            active: false,
            widgetVisible: true,
            useMinMax: null,
            clicked: false,
        };

        $scope.onOutsidePopupClick = () => {
            if ($scope.popup.active) {
                $scope.popup.active = false;
                $scope.popup.clicked = true;
                $scope.$applyAsync();
                //this allows it to opened after closing it by clicking outside
                setTimeout(() => { $scope.popup.clicked = false; $scope.$applyAsync(); }, 1);
                return true;
            } else {
                return false;
            }
        };

        $scope.clickSettings = function () {
            if (!$scope.popup.clicked && !$scope.popup.active) {
                $scope.popup.active = true;
                $scope.popup.clicked = true;
                $scope.$applyAsync();
            }
        };

        $scope.$watch('popup.widgetVisible', (isVisible) => {
            if ($scope.itemState) $scope.itemState.active = isVisible;
        });

        $scope.clickExpand = () => {
            if ($scope.itemState) $scope.itemState.expanded = !$scope.itemState.expanded;
        }

        // Only do a data load every 300 ms, to prevent multiple loads
        let dataLoadTimeout = null;
        $q.all([GraphService.ready(), locale.ready('advanced')]).then(function () {
            function load() {
                // Only load data if tab is active in browser
                if ((typeof document.hidden != 'undefined' && document.hidden) && loaded) return;
                clearTimeout(dataLoadTimeout);
                dataLoadTimeout = setTimeout(() => {

                    if ($scope.inview && !loaded && $scope.graphData) {
                        loaded = true;

                        const currentCopy = cloneDeep($scope.current)
                        const initialFields = { ...currentCopy.fields }
                        Object.keys($scope.graphData.data).forEach(key => {
                            if (!$scope.graphData.data[key].length) {
                                const seriesCode = $scope.graphData.meta[key].code
                                delete currentCopy.fields[seriesCode]
                            }
                        })

                        if (Object.keys(currentCopy.fields).length < 3 && Object.keys(currentCopy.fields).includes('TOTAL') && !currentCopy.totalOnly) {
                            delete currentCopy.fields['TOTAL']
                        }

                        // Never change initial loaded chart
                        if (!$scope.chart) {
                            // fields can be deleted if there is no data
                            // but we want initial config to be based on all possible fields
                            $scope.chart = GraphService.getAdvancedConfig(
                                {
                                    ...currentCopy,
                                    fields: initialFields,
                                },
                                getGraphColors()
                            );
                            $scope.popup.useMinMax = handleInitialLocalStorageBool(localStorageService.get(storageKey()), "use_range");
                        }

                        // Note: keep in sync with graph-widget.directive.js
                        const parsedMetas = cloneDeep($scope.graphData.meta);

                        $scope.metas = [];
                        if (!currentCopy.totalOnly) {
                            $scope.metas = _.filter(parsedMetas, function (field) {
                                return currentCopy.fields.hasOwnProperty(field.code);
                            });
                            $scope.metas = _.sortBy($scope.metas, function (meta) {
                                if (currentCopy.fields[meta.code] && currentCopy.fields[meta.code].yAxis)
                                    return currentCopy.fields[meta.code].yAxis;
                                return 0;
                            });
                        }

                        if (currentCopy.allowSum && !currentCopy.totalOnly && Object.keys(currentCopy.fields).includes('TOTAL')) {
                            $scope.metas.push({
                                'description': locale.getString('advanced.total'),
                                'formatWithUnit': '',
                                'formatValueOnly': ''
                            })
                        }

                        if (currentCopy.preprocessMeta) {
                            $scope.metas = currentCopy.preprocessMeta($scope.metas);
                        }

                        GraphService.updateConfig(
                            $scope.chart,
                            $scope.graphData.data,
                            parsedMetas,
                            initialFields,
                            !!currentCopy.allowSum,
                            !!currentCopy.totalOnly,
                            $scope.range,
                            currentCopy.preprocessValue,
                            $scope.popup.useMinMax,
                        );

                        mixinZoomState($scope, $scope.setWatcher);
                        $scope.setWatcher = true;
                    }
                }, 300)
            }

            $scope.$watchGroup(['installation', 'graphData', 'current', 'popup.useMinMax'], function () {
                loaded = false;
                if ($scope.installation == null) {
                    return;
                }
                updateStorageRanges();
                load();
            });

            $scope.$watchGroup(['inview'], load);
        });

        // Summary methods
        //Create list of used fields for extended
        $scope.extended = [];
        if (AdvancedSummaryGraphWidgetConfigsSummaries[$scope.type]) {
            $scope.extended.push(AdvancedSummaryGraphWidgetConfigsSummaries[$scope.type].status);
            for (var i in AdvancedSummaryGraphWidgetConfigsSummaries[$scope.type].extended) {
                if (typeof AdvancedSummaryGraphWidgetConfigsSummaries[$scope.type].extended[i] !== "function") {
                    for (var j in AdvancedSummaryGraphWidgetConfigsSummaries[$scope.type].extended[i]) {
                        for (var n in AdvancedSummaryGraphWidgetConfigsSummaries[$scope.type].extended[i][j]) {
                            $scope.extended.push(AdvancedSummaryGraphWidgetConfigsSummaries[$scope.type].extended[i][j][n]);
                        }
                    }
                }
            }
            for (var i in AdvancedSummaryGraphWidgetConfigsSummaries[$scope.type].subdata) {
                $scope.extended.push(AdvancedSummaryGraphWidgetConfigsSummaries[$scope.type].subdata[i]);
            }
            for (var i in AdvancedSummaryGraphWidgetConfigsSummaries[$scope.type].hide) {
                $scope.extended.push(AdvancedSummaryGraphWidgetConfigsSummaries[$scope.type].hide[i]);
            }
        }

        $scope.widgetConfig = AdvancedSummaryGraphWidgetConfigsSummaries[$scope.type];
        $scope.extra = {}; //Extra data from other API's

        $scope.attributeOrder = [];

        $scope.sortAttributes = function (record) {
            // Sort by the current attribute order (retrieved from API)
            return $scope.attributeOrder.indexOf(parseInt(record.idDataAttribute));
        };

        $scope.$watch('installation', function (installation) {
            if (installation != null) {
                var url = 'widgets/' + $scope.type;
                if ($scope.instance) {
                    url += '?instance=' + $scope.instance;
                }
                ApiService.installations(installation.id, url).then(function (response) {
                    $scope.data = _.clone(response.data);
                    $scope.attributeOrder = response.data.records.attributeOrder;
                    var data = {};
                    // Set up MQTT for realtime data
                    _.each(response.data.records.data, function (record, key) {
                        if (!record.code) {
                            data[key] = record;
                            return;
                        }
                        if (!record.dataType || !record.dbusPath) {
                            data[record.code] = record;
                            return;
                        }
                        record.mqttFetcher = _.bind(function (message) {
                            // Skip formatting if payload value is null
                            if (message.payload.value === null) return;

                            var value = MQTTRealtimeService.convertValue(this.code, message.payload.value);
                            var isEnum = false;
                            if (typeof this.dataAttributeEnumValues !== "undefined") {
                                _.each(this.dataAttributeEnumValues, function (enumVal) {
                                    if (enumVal.valueEnum == value) {
                                        value = enumVal.nameEnum;
                                        isEnum = true;
                                    }
                                });
                            }
                            // Calculate value
                            this.valueFormattedValueOnly = isEnum ? value : sprintf(this.formatValueOnly.replace('F', 'f'), value);
                            this.valueFormattedWithUnit = isEnum ? value : sprintf(this.formatWithUnit.replace('F', 'f'), value);
                        }, record);

                        if (vrmConfig.enableRealtime) {
                            MQTTRealtimeService.subscribeToPath({
                                identifier: $scope.installation.identifier,
                                dbusServiceType: record.dbusServiceType,
                                dbusPath: record.dbusPath,
                                instance: record.instance,
                                callback: record.mqttFetcher,
                                callbackIdentifier: "summarywidget_" + $scope.type
                            });
                        }
                        data[record.code] = record;
                    });

                    $scope.data.records.data = data;
                });
                if ($scope.widgetConfig) {
                    for (var index in $scope.widgetConfig.urls) {
                        ApiService.installations(installation.id, $scope.widgetConfig.urls[index]).then(function (response) {
                            $scope.extra[index] = response.data;
                        });
                    }
                }
            }
        });


        /**
         * Get type of item
         * @param item
         * @returns {string}
         */
        $scope.typeOf = function (item) {
            return typeof item;
        };

        /**
         * Get a field from the retrieved data
         * @param fieldName
         * @returns {*}
         */
        $scope.getField = function (fieldName) {
            if ($scope.data && $scope.data.records) {
                return _.find($scope.data.records.data, { code: fieldName });
            } else {
                return null;
            }
        };

        /**
         * Return if field is a current value
         * @param format
         */
        $scope.isCurrent = function (format) {
            //Check on A in format unit and exclude VA
            return format && format.indexOf('A') >= 0 && format.indexOf('V') === -1;
        };

        $scope.$on('$destroy', function () {
            clearTimeout(dataLoadTimeout);
        });

        function storageKey() {
            return ['use_ranges', $scope.installation.id, $scope.widgetId, $scope.instance].join('_');
        }

        function getGraphColors() {
            if (!$scope.current.getColors) {
                return null;
            }
            return $scope.current.getColors(WhitelabelService.getGraphColors());
        }


        function updateStorageRanges() {
            let old_value = localStorageService.get(storageKey())
            
            if ($scope.popup.useMinMax === null) { // value === null
                // no value in parameter is updated with the local storage value
                if (old_value && old_value["use_range"] !== null) {  
                    $scope.popup.useMinMax = old_value["use_range"] // initial value 
                }
            }
            else {
                const result = handleLocalStorageBool( old_value, $scope.popup.useMinMax, "use_range")
                if(result){
                    localStorageService[result.action](storageKey(), result.value);
                }
            }
        }
    }
}
